import React, { useContext, useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import useProducts from '../hooks/useProducts';
import { CartContext } from '../context/CartContext';
import { trackViewContent, trackAddToCart } from '../utils/fbPixel';
import GoBackButton from './GoBackButton';

// Builds the ordered list of attributes (color, size, etc.) with the union of
// all values seen across every combination of this product. Order follows
// the order attributes first appear in the variants array, which mirrors the
// OMS mpa_id ordering already applied server-side.
function buildAttributesList(variants) {
  const map = new Map();
  variants.forEach(v => {
    v.attributes.forEach(a => {
      if (!map.has(a.attrId)) {
        map.set(a.attrId, { attrId: a.attrId, attrName: a.attrName, values: new Map() });
      }
      map.get(a.attrId).values.set(a.variantId, a.variantName);
    });
  });
  return Array.from(map.values());
}

// Combinations matching the current selection. An attribute counts as a
// match only if the combination actually carries that attribute with the
// selected value — combinations that don't include a given attribute at all
// are excluded once that attribute has a selection.
function getMatchingVariants(variants, selection) {
  const selectedEntries = Object.entries(selection).filter(([, v]) => v != null);
  if (selectedEntries.length === 0) return variants;

  return variants.filter(v =>
    selectedEntries.every(([attrId, variantId]) => {
      const attr = v.attributes.find(a => String(a.attrId) === String(attrId));
      return attr && String(attr.variantId) === String(variantId);
    })
  );
}

// For one attribute's dropdown, which of its values would still lead to at
// least one matching combination, given everything else currently selected
// (excluding this attribute's own selection).
function getAvailableValues(variants, selection, attrId) {
  const otherSelection = { ...selection };
  delete otherSelection[attrId];
  const matching = getMatchingVariants(variants, otherSelection);
  const available = new Set();
  matching.forEach(v => {
    const attr = v.attributes.find(a => String(a.attrId) === String(attrId));
    if (attr) available.add(String(attr.variantId));
  });
  return available;
}

export default function ProductDetails() {
  const { businessName, salePointName, productId } = useParams();
  const { products, loading } = useProducts(businessName, salePointName);
  const { addToCart, cartItems } = useContext(CartContext);

  const [variants, setVariants] = useState([]);
  const [variantsLoading, setVariantsLoading] = useState(false);
  const [selection, setSelection] = useState({}); // attrId -> variantId

  const [stockQuantity, setStockQuantity] = useState(null); // null = not checked yet
  const [stockLoading, setStockLoading] = useState(false);

  const product = !loading ? products.find((p) => p.id.toString() === productId) : null;

  useEffect(() => {
    if (product) {
      trackViewContent(product);
    }
  }, [product]);

  useEffect(() => {
    if (!product || !product.hasVariants) return;

    setVariantsLoading(true);
    fetch(`/api/products/${businessName}/${salePointName}/${product.id}/variants`)
      .then(res => res.json())
      .then(data => setVariants(Array.isArray(data) ? data : []))
      .catch(err => {
        console.error('Failed to fetch variants', err);
        setVariants([]);
      })
      .finally(() => setVariantsLoading(false));
  }, [product, businessName, salePointName]);

  const attributesList = useMemo(() => buildAttributesList(variants), [variants]);

  const matchingVariants = useMemo(
    () => getMatchingVariants(variants, selection),
    [variants, selection]
  );

  const hasVariants = useMemo(
    () => !!product?.hasVariants && attributesList.length > 0,
    [product, attributesList]
  );

  const allAttributesSelected =
    attributesList.length > 0 && attributesList.every(attr => selection[attr.attrId]);

  const selectedVariant =
    allAttributesSelected && matchingVariants.length === 1 ? matchingVariants[0] : null;

  // Live stock check — plain products check once on load, variant products
  // check right after a full combination is selected. Nothing is cached;
  // this always reflects what the OMS reports at this exact moment.
  useEffect(() => {
    if (!product) return;

    // If the OMS marked this product as "sell if not provided in store",
    // the storefront must not request or display live stock — hide quantity
    // but still allow adding to cart.
    if (product.sellIfNotProvidedInStore) {
      setStockQuantity(null);
      return;
    }

    if (hasVariants && !selectedVariant) {
      setStockQuantity(null);
      return;
    }

    const combId = hasVariants ? selectedVariant.combinationId : 0;

    setStockLoading(true);
    fetch(`/api/products/${businessName}/${salePointName}/stock`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ itemId: product.productOriginalId, combId }]
      })
    })
      .then(res => res.json())
      .then(data => {
        const result = Array.isArray(data) ? data[0] : null;
        setStockQuantity(result && typeof result.quantity === 'number' ? result.quantity : 0);
      })
      .catch(err => {
        console.error('Failed to fetch stock', err);
        setStockQuantity(null);
      })
      .finally(() => setStockLoading(false));
  }, [product, hasVariants, selectedVariant, businessName, salePointName]);

  const handleSelectValue = (attrId, variantId) => {
    setSelection(prev => ({ ...prev, [attrId]: variantId || undefined }));
  };

  if (loading) return <div className="loading">Loading product details...</div>;
  if (!product) return <div className="error">Product not found.</div>;

  const imageUrl = product.imageUrl;

  const needsSelection = hasVariants && !selectedVariant;
  const outOfStock = stockQuantity === 0;

  // For restricted items (sellIfNotProvidedInStore = false), check if adding
  // would exceed available quantity. For unrestricted items (true), allow unlimited adds.
  let quantityExceeded = false;
  if (!product.sellIfNotProvidedInStore && stockQuantity !== null && stockQuantity > 0) {
    // For variant products, only check if a full combination is selected
    if (!needsSelection) {
      const cartLineId = hasVariants
        ? `${product.id}-${selectedVariant.combinationId}`
        : `${product.id}`;
      const itemInCart = cartItems.find(item => item.cartLineId === cartLineId);
      const alreadyInCart = itemInCart ? itemInCart.quantity : 0;
      quantityExceeded = alreadyInCart >= stockQuantity;
    }
  }

  const canAddToCart = !needsSelection && !outOfStock && !stockLoading && !quantityExceeded;

  const handleAddToCart = () => {
    if (!canAddToCart) return;

    const cartProduct = hasVariants
      ? {
          ...product,
          combinationId: selectedVariant.combinationId,
          combinationText: selectedVariant.combinationNameAr,
          stockQuantity, // Include live-fetched stock for this combination
        }
      : {
          ...product,
          stockQuantity, // Include live-fetched stock
        };

    addToCart(cartProduct);
    trackAddToCart(cartProduct);
  };

  let buttonLabel = 'أضف إلي العربة';
  if (needsSelection) buttonLabel = 'يرجى اختيار التشكيلة';
  else if (stockLoading) buttonLabel = 'جاري التحقق من الكمية...';
  else if (outOfStock) buttonLabel = 'نفذت الكمية';
  else if (quantityExceeded) buttonLabel = 'تم إضافة الكمية المتاحة';

  return (
    <>
      <div style={{ padding: '20px' }}>
        <GoBackButton />
      </div>
      <div className="product-card-details">
        <div className="image-section">
          <img src={imageUrl} alt={product.name} className="details-image" />
        </div>
        <div className="info-section">
          <h2 className="product-name">{product.name}</h2>
          <p className="product-description">{product.description}</p>
          <p className="product-price">Price: <strong>IQD {product.price.toFixed(2)}</strong></p>

          {hasVariants && (
            <div className="variant-picker">
              {variantsLoading && <p>جاري تحميل التشكيلات...</p>}

              {!variantsLoading && attributesList.map(attr => {
                const availableValues = getAvailableValues(variants, selection, attr.attrId);
                const selectedValue = selection[attr.attrId] || '';

                return (
                  <div key={attr.attrId} className="variant-attribute">
                    <label className="variant-attribute-label">{attr.attrName}</label>
                    <div className="variant-options">
                      {Array.from(attr.values.entries()).map(([variantId, variantName]) => {
                        const isAvailable = availableValues.has(String(variantId));
                        const isSelected = String(selectedValue) === String(variantId);

                        return (
                          <button
                            type="button"
                            key={variantId}
                            disabled={!isAvailable}
                            className={
                              'variant-option' +
                              (isSelected ? ' variant-option-selected' : '') +
                              (!isAvailable ? ' variant-option-disabled' : '')
                            }
                            onClick={() =>
                              handleSelectValue(
                                attr.attrId,
                                isSelected ? null : variantId
                              )
                            }
                          >
                            {variantName}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!product.sellIfNotProvidedInStore && !needsSelection && !stockLoading && stockQuantity !== null && (
            <p className={outOfStock ? 'stock-text stock-out' : 'stock-text stock-available'}>
              {outOfStock ? 'نفذت الكمية' : `الكمية المتوفرة: ${stockQuantity}`}
            </p>
          )}

          <button
            onClick={handleAddToCart}
            className="add-to-cart-btn"
            disabled={!canAddToCart}
          >
            {buttonLabel}
          </button>
        </div>
      </div>
    </>
  );
}